import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BookOpen, User, Upload, Shield, LogOut, LogIn, Crown, BarChart3, Trophy, Menu, X, Moon, Sun, Globe, Gift, Users, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import SubscriptionBadge from './SubscriptionBadge';
import NotificationBell from './NotificationBell';
import LanguageToggle from './LanguageToggle';
import BedtimeToggle from './BedtimeToggle';
import ProfilePicker from './ProfilePicker';
import { Button } from './ui/button';
import { usePlatform } from '../hooks/usePlatform';

const Navigation = () => {
  const location = useLocation();
  const { user, logout, isAuthenticated } = useAuth();
  const { isPremium } = useSubscription();
  const { t } = useLanguage();
  const { isBedtimeMode, bedtimeMode, setBedtimeMode } = useTheme();
  const { hideWebPurchaseUI } = usePlatform();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Desktop Navigation - Top Bar */}
      <nav className="hidden md:block fixed top-0 left-0 right-0 z-[100] bg-card/95 backdrop-blur-md border-b border-border shadow-sm" data-testid="nav-desktop">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
              <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-gold" />
              </div>
              <span className="font-display text-2xl text-foreground">
                {t('library.title')}
              </span>
            </Link>

            <div className="flex items-center gap-3">
              <Link to="/">
                <Button data-testid="nav-library" variant={isActive('/') ? 'default' : 'ghost'} size="sm" className="rounded-full">
                  <BookOpen className="w-4 h-4" />
                  <span className="ml-2">{t('nav.library')}</span>
                </Button>
              </Link>
              <Link to="/top">
                <Button data-testid="nav-top-stories" variant={isActive('/top') ? 'default' : 'ghost'} size="sm" className="rounded-full">
                  <Trophy className="w-4 h-4" />
                  <span className="ml-2">{t('nav.top10')}</span>
                </Button>
              </Link>
              {isAuthenticated && (
                <Link to="/garden">
                  <Button data-testid="nav-garden" variant={isActive('/garden') ? 'default' : 'ghost'} size="sm" className="rounded-full">
                    <Sparkles className="w-4 h-4" />
                    <span className="ml-2">Tuintje</span>
                  </Button>
                </Link>
              )}
              {!isPremium && !hideWebPurchaseUI && (
                <Link to="/subscription">
                  <Button data-testid="nav-premium" variant="ghost" size="sm" className="rounded-full bg-gold/10 hover:bg-gold/20 text-gold">
                    <Crown className="w-4 h-4" />
                    <span className="ml-2">{t('nav.premium')}</span>
                  </Button>
                </Link>
              )}
              {isAuthenticated && (
                <>
                  <Link to="/profile">
                    <Button data-testid="nav-profile" variant={isActive('/profile') ? 'default' : 'ghost'} size="sm" className="rounded-full">
                      <User className="w-4 h-4" />
                      <span className="ml-2">{t('nav.profile')}</span>
                    </Button>
                  </Link>
                  <Link to="/upload">
                    <Button data-testid="nav-upload" variant={isActive('/upload') ? 'default' : 'ghost'} size="sm" className="rounded-full">
                      <Upload className="w-4 h-4" />
                      <span className="ml-2">{t('nav.upload')}</span>
                    </Button>
                  </Link>
                  {user?.is_admin && (
                    <>
                      <Link to="/admin">
                        <Button data-testid="nav-admin" variant={isActive('/admin') ? 'default' : 'ghost'} size="sm" className="rounded-full">
                          <Shield className="w-4 h-4" />
                          <span className="ml-2">{t('nav.stories')}</span>
                        </Button>
                      </Link>
                      <Link to="/platform">
                        <Button data-testid="nav-platform" variant={isActive('/platform') ? 'default' : 'ghost'} size="sm" className="rounded-full">
                          <BarChart3 className="w-4 h-4" />
                          <span className="ml-2">{t('nav.platform')}</span>
                        </Button>
                      </Link>
                    </>
                  )}
                  <SubscriptionBadge />
                  <NotificationBell />
                </>
              )}
              <LanguageToggle />
              <BedtimeToggle />
              {isAuthenticated && <ProfilePicker />}
              {isAuthenticated ? (
                <Button data-testid="nav-logout" variant="ghost" size="sm" onClick={logout} className="rounded-full text-destructive hover:text-destructive">
                  <LogOut className="w-4 h-4" />
                  <span className="ml-2">{t('nav.logout')}</span>
                </Button>
              ) : (
                <Link to="/login">
                  <Button data-testid="nav-login" size="sm" className="rounded-full bg-old-rose hover:bg-old-rose/80 text-night-blue font-bold">
                    <LogIn className="w-4 h-4" />
                    <span className="ml-2">{t('nav.login')}</span>
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation - Bottom Bar */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-[100] bg-card/95 backdrop-blur-md border-t border-border shadow-lg" data-testid="nav-mobile">
        <div className="flex items-center justify-around px-2 py-2">
          <Link to="/" className="flex-1" onClick={() => setMobileMenuOpen(false)}>
            <button data-testid="nav-library-mobile" className={`w-full flex flex-col items-center gap-0.5 py-1 rounded-lg transition-colors ${isActive('/') ? 'text-gold' : 'text-muted-foreground'}`}>
              <BookOpen className="w-5 h-5" />
              <span className="text-[10px] font-medium leading-tight">{t('nav.library')}</span>
            </button>
          </Link>
          <Link to="/top" className="flex-1" onClick={() => setMobileMenuOpen(false)}>
            <button data-testid="nav-top-mobile" className={`w-full flex flex-col items-center gap-0.5 py-1 rounded-lg transition-colors ${isActive('/top') ? 'text-gold' : 'text-muted-foreground'}`}>
              <Trophy className="w-5 h-5" />
              <span className="text-[10px] font-medium leading-tight">{t('nav.top10')}</span>
            </button>
          </Link>
          {isAuthenticated && (
            <Link to="/garden" className="flex-1" onClick={() => setMobileMenuOpen(false)}>
              <button data-testid="nav-garden-mobile" className={`w-full flex flex-col items-center gap-0.5 py-1 rounded-lg transition-colors ${isActive('/garden') ? 'text-gold' : 'text-muted-foreground'}`}>
                <Sparkles className="w-5 h-5" />
                <span className="text-[10px] font-medium leading-tight">Tuintje</span>
              </button>
            </Link>
          )}
          {isAuthenticated ? (
            <>
              <Link to="/profile" className="flex-1" onClick={() => setMobileMenuOpen(false)}>
                <button data-testid="nav-profile-mobile" className={`w-full flex flex-col items-center gap-0.5 py-1 rounded-lg transition-colors ${isActive('/profile') ? 'text-gold' : 'text-muted-foreground'}`}>
                  <User className="w-5 h-5" />
                  <span className="text-[10px] font-medium leading-tight">{t('nav.profile')}</span>
                </button>
              </Link>
              <Link to="/upload" className="flex-1" onClick={() => setMobileMenuOpen(false)}>
                <button data-testid="nav-upload-mobile" className={`w-full flex flex-col items-center gap-0.5 py-1 rounded-lg transition-colors ${isActive('/upload') ? 'text-gold' : 'text-muted-foreground'}`}>
                  <Upload className="w-5 h-5" />
                  <span className="text-[10px] font-medium leading-tight">{t('nav.upload')}</span>
                </button>
              </Link>
            </>
          ) : (
            <Link to="/login" className="flex-1" onClick={() => setMobileMenuOpen(false)}>
              <button data-testid="nav-login-mobile" className={`w-full flex flex-col items-center gap-0.5 py-1 rounded-lg transition-colors ${isActive('/login') ? 'text-gold' : 'text-muted-foreground'}`}>
                <LogIn className="w-5 h-5" />
                <span className="text-[10px] font-medium leading-tight">{t('nav.login')}</span>
              </button>
            </Link>
          )}
          <button
            data-testid="nav-mobile-menu"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`flex-1 flex flex-col items-center gap-0.5 py-1 rounded-lg transition-colors ${mobileMenuOpen ? 'text-gold' : 'text-muted-foreground'}`}
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            <span className="text-[10px] font-medium leading-tight">Menu</span>
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <>
          <div className="md:hidden fixed inset-0 bg-black/40 z-[90]" onClick={() => setMobileMenuOpen(false)} />
          <div className="md:hidden fixed bottom-[60px] left-3 right-3 z-[95] bg-card border border-border rounded-2xl shadow-2xl p-4 max-h-[70vh] overflow-y-auto" data-testid="mobile-menu-panel">
            <div className="flex flex-col gap-1">
              {/* Bedtime Mode — 3 stand: uit / auto / aan */}
              <div className="px-2 pt-2 pb-3 border-b border-border mb-2">
                <div className="flex items-center gap-2 mb-2">
                  {isBedtimeMode ? <Moon className="w-4 h-4 text-amber-400" /> : <Sun className="w-4 h-4 text-amber-500" />}
                  <span className="text-sm font-display text-foreground">Bedtime Mode</span>
                </div>
                <div className="flex gap-1.5">
                  {[['off','Uit'],['auto','Auto'],['on','Aan']].map(([val,label]) => (
                    <button
                      key={val}
                      data-testid={`bedtime-mode-mobile-${val}`}
                      onClick={() => setBedtimeMode(val)}
                      className={`flex-1 px-2 py-1.5 rounded-full text-[11px] font-bold transition-colors ${
                        bedtimeMode === val
                          ? 'bg-old-rose text-night-blue shadow'
                          : 'bg-muted/40 text-muted-foreground'
                      }`}
                    >{label}</button>
                  ))}
                </div>
              </div>

              {/* Gift Link (hidden on iOS — Reader App compliance) */}
              {!hideWebPurchaseUI && (
                <Link to="/gift" onClick={() => setMobileMenuOpen(false)}>
                  <div className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-muted text-old-rose transition-colors">
                    <Gift className="w-5 h-5" />
                    <span className="text-sm font-medium">Cadeau geven</span>
                  </div>
                </Link>
              )}

              {/* Subscription (hidden on iOS — Reader App compliance) */}
              {!isPremium && isAuthenticated && !hideWebPurchaseUI && (
                <Link to="/subscription" onClick={() => setMobileMenuOpen(false)}>
                  <div className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-muted text-gold transition-colors">
                    <Crown className="w-5 h-5" />
                    <span className="text-sm font-medium">{t('nav.premium')}</span>
                  </div>
                </Link>
              )}

              {/* Admin Links */}
              {user?.is_admin && (
                <>
                  <Link to="/admin" onClick={() => setMobileMenuOpen(false)}>
                    <div className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${isActive('/admin') ? 'bg-gold/10 text-gold' : 'hover:bg-muted text-foreground'}`}>
                      <Shield className="w-5 h-5" />
                      <span className="text-sm font-medium">{t('nav.manage_stories')}</span>
                    </div>
                  </Link>
                  <Link to="/platform" onClick={() => setMobileMenuOpen(false)}>
                    <div className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${isActive('/platform') ? 'bg-gold/10 text-gold' : 'hover:bg-muted text-foreground'}`}>
                      <BarChart3 className="w-5 h-5" />
                      <span className="text-sm font-medium">{t('nav.platform_stats')}</span>
                    </div>
                  </Link>
                </>
              )}

              {/* Divider */}
              <div className="h-px bg-border my-1" />

              {/* Language Selector inline */}
              <MobileLanguageSelector onClose={() => setMobileMenuOpen(false)} />

              {/* Logout */}
              {isAuthenticated && (
                <button
                  data-testid="nav-logout-mobile"
                  onClick={() => { logout(); setMobileMenuOpen(false); }}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-destructive/10 text-destructive transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="text-sm font-medium">{t('nav.logout')}</span>
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

const MobileLanguageSelector = ({ onClose }) => {
  const { lang, setLanguage } = useLanguage();
  const languages = [
    { code: 'nl', label: 'NL' },
    { code: 'en', label: 'EN' },
    { code: 'de', label: 'DE' },
    { code: 'fr', label: 'FR' },
  ];

  return (
    <div className="flex items-center gap-2 px-4 py-3">
      <Globe className="w-5 h-5 text-muted-foreground shrink-0" />
      <div className="flex gap-1.5 flex-1">
        {languages.map((l) => (
          <button
            key={l.code}
            data-testid={`lang-mobile-${l.code}`}
            onClick={() => { setLanguage(l.code); onClose(); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${
              lang === l.code ? 'bg-gold/20 text-gold' : 'bg-muted text-muted-foreground hover:text-foreground'
            }`}
          >
            {l.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default Navigation;
